<template>
  <dx-modal :value="show" :title="title" size="xl" :closable="!busy"
            @input="close()" id="modal-appeal">
    <div class="billing-gloss appeal-modal">
      <div class="loading loading-lg" v-if="loading"></div>
      <template v-else-if="view">
        <div class="bg-appeal-head">
          <span class="bg-pill bg-pill-outline">{{ roundName }}</span>
          <span class="bg-pill" :class="statusClass">
            {{ statusName }}<template v-if="appeal"> {{ appeal.number }}</template>
          </span>
          <span class="text-xs text-gray">
            · {{ insuranceName }}
            <template v-if="tissVersion"> · TISS {{ tissVersion }}</template>
            ·
            {{ xmlAvailable ? 'recurso eletrônico disponível' : 'envio pelo portal da operadora' }}
          </span>
        </div>

        <div class="bg-note bg-note-amber" v-if="tissOld">
          Este convênio está em TISS
          {{ tissVersion || 'não informado' }}: o recurso eletrônico só existe a partir
          da 4.01.00. Registre aqui para acompanhar e envie pelo portal da operadora.
        </div>
        <div class="text-xs text-gray mb-1" v-else-if="tissWarn">
          TISS 4.03.00 é obrigatória desde 07/2026; o lote está em {{ tissVersion }}.
        </div>
        <div class="bg-note bg-note-amber" v-if="deadlineOverdue && !readonly">
          <fa-icon :icon="['fal', 'exclamation-triangle']"/>
          Prazo contratual vencido em {{ deadlineInfo.at }}. Envie mesmo assim ou aceite a glosa.
        </div>
        <div class="bg-note bg-note-info" v-if="addedMessage">{{ addedMessage }}</div>

        <div class="cr-box bg-appeal-box">
          <div class="bg-appeal-grid">
            <div class="form-group">
              <label class="form-label">Objeto do recurso</label>
              <div class="btn-group bg-seg" v-if="!readonly">
                <button class="btn btn-sm" :class="form.objectType === 'guide' ? 'btn-primary' :
                'btn-gray'"
                        @click="form.objectType = 'guide'">Guias e itens</button>
                <button class="btn btn-sm" :class="form.objectType === 'protocol' ? 'btn-primary' :
                'btn-gray'"
                        @click="form.objectType = 'protocol'">Protocolo inteiro</button>
              </div>
              <div class="bg-readonly" v-else>
                {{ form.objectType === 'protocol' ? 'Protocolo inteiro' : 'Guias e itens' }}
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Nº do recurso</label>
              <div class="bg-readonly">{{ appeal ? appeal.number : 'gerado ao salvar' }}</div>
            </div>
            <div class="form-group">
              <label class="form-label">
                Nº na operadora <span class="text-gray">(opcional)</span>
              </label>
              <input type="text" class="form-input" v-model="form.operatorNumber" maxlength="20"
                     v-if="!readonly" placeholder="numeroGuiaRecGlosaOperadora">
              <div class="bg-readonly" v-else>{{ form.operatorNumber || '—' }}</div>
            </div>
            <div class="form-group" :class="{'has-error': $v.form.appealAt.$error}">
              <label class="form-label">Data do recurso</label>
              <dx-input-date id="appeal-date" class="form-input" v-model="form.appealAt"
                             v-if="!readonly" @blur="$v.form.appealAt.$touch()"/>
              <div class="bg-readonly" v-else>{{ form.appealAt | date }}</div>
              <div class="form-input-hint" v-if="$v.form.appealAt.$error">Data inválida</div>
            </div>
          </div>
          <div class="text-xs text-gray">
            <template v-if="batch.protocol && batch.protocol.number">
              Protocolo {{ batch.protocol.number }} ·
            </template>
            Lote {{ batch.number }}
            <template v-if="batch.dates && batch.dates.sentAt">
              enviado em {{ batch.dates.sentAt | date }}
            </template>
            <template v-if="deadlineInfo.at">
              · prazo contratual até {{ deadlineInfo.at }}
            </template>
          </div>
        </div>

        <div class="columns bg-appeal-columns">
          <div class="column col-8 col-md-12">
            <template v-if="form.objectType === 'protocol'">
              <div class="cr-panel cr-panel-first">
                <div class="cr-panel-head">
                  <div class="cr-panel-title cr-panel-title-sm">Recurso do protocolo inteiro</div>
                  <div class="cr-panel-subtitle">
                    Uma única justificativa para o protocolo. Os itens selecionados (
                    {{ items.length }}) entram como
                    glosas recursadas com o valor em aberto.
                  </div>
                </div>
                <div class="cr-panel-body">
                  <div class="form-group"
                       :class="{'has-error': $v.form.protocolAppeal.glossCode.$error}">
                    <label class="form-label">Código da glosa do protocolo</label>
                    <gloss-code-autocomplete id="appeal-protocol-code"
                    v-model="form.protocolAppeal.glossCode"
                                             :insurance-id="insuranceId" :disabled="readonly"
                                             @blur="$v.form.protocolAppeal.glossCode.$touch()"/>
                    <div class="form-input-hint" v-if="$v.form.protocolAppeal.glossCode.$error">
                      Campo obrigatório
                    </div>
                  </div>
                  <div class="form-group"
                       :class="{'has-error': $v.form.protocolAppeal.justification.$error}">
                    <label class="form-label">Justificativa</label>
                    <textarea class="form-input" rows="4"
                    v-model="form.protocolAppeal.justification"
                              :maxlength="maxLength" :disabled="readonly"
                              @blur="$v.form.protocolAppeal.justification.$touch()"></textarea>
                    <div class="form-input-hint" v-if="$v.form.protocolAppeal.justification.$error">
                      Campo obrigatório
                    </div>
                    <div class="text-xs text-gray text-right"
                         :class="{ 'text-red': protocolLength >= 480 }">
                      {{ protocolLength }}/{{ maxLength }}
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="cr-panel" :class="{ 'cr-panel-first': groupIndex === 0 }"
                   v-for="(group, groupIndex) in groups" :key="group.accountId">
                <div class="cr-panel-head">
                  <div class="cr-panel-title cr-panel-title-sm">
                    {{ group.guideName }}
                    <span class="text-gray bg-guide-sub">
                      · {{ group.patientName }}<template v-if="group.senha"> · senha
                      {{ group.senha }}</template>
                    </span>
                  </div>
                  <div class="cr-panel-right">
                    <span class="bg-count">
                      {{ group.items.length }} {{ group.items.length === 1 ? 'item' : 'itens' }}
                    </span>
                  </div>
                </div>
                <appeal-item-card
                  v-for="entry in group.items" :key="entry.item.glossId"
                  :ref="`item-${entry.item.glossId}`"
                  :item="entry.item"
                  :validator="$v.items.$each[entry.index]"
                  :justifications="justifications[entry.item.glossCode] || []"
                  :loading-justifications="loadingCode === entry.item.glossCode"
                  :same-code-count="sameCodeCount(entry.item)"
                  :readonly="readonly"
                  :removable="items.length > 1"
                  :max-length="maxLength"
                  @load-justifications="loadJustifications"
                  @remove-justification="removeJustification"
                  @apply-to-code="applyToCode"
                  @remove="removeItem(entry.item)"
                />
              </div>
              <div class="cr-panel cr-panel-first" v-if="groups.length === 0">
                <div class="empty cr-panel-empty">
                  <div class="empty-icon"><fa-icon :icon="['fal', 'gavel']" size="2x"/></div>
                  <p class="empty-subtitle">
                    Nenhum item neste recurso. Adicione as glosas abertas do lote.
                  </p>
                </div>
              </div>
              <div class="cr-panel bg-add-panel" v-if="!readonly">
                <div class="bg-add-items">
                  <button class="btn btn-secondary btn-sm btn-icon btn-icon-left"
                          :disabled="available.length === 0" @click="adding.show = !adding.show">
                    <fa-icon :icon="['fal', 'plus']"/>Adicionar itens
                  </button>
                  <span class="text-xs text-gray">
                    <template v-if="available.length === 0">
                      Todas as glosas recursáveis do lote já estão neste recurso.
                    </template>
                    <template v-else>
                      {{ available.length }}
                      {{ available.length === 1 ? 'glosa aberta' : 'glosas abertas' }}
                      do lote ainda fora deste recurso.
                    </template>
                  </span>
                </div>
                <div class="cr-table-scroll" v-if="adding.show && available.length > 0">
                  <table class="table">
                    <thead>
                    <tr>
                      <th class="bg-check"></th>
                      <th>Guia</th>
                      <th>Item</th>
                      <th>Glosa</th>
                      <th class="text-right">Em aberto</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="candidate in available" :key="candidate.glossId"
                        :class="{ active: adding.selected.includes(candidate.glossId) }">
                      <td class="bg-check">
                        <label class="form-checkbox">
                          <input type="checkbox" :value="candidate.glossId"
                                 v-model="adding.selected">
                          <i class="form-icon"></i>
                        </label>
                      </td>
                      <td>
                        {{ candidate.guideName }} <small class="text-gray">·
                        {{ candidate.patientName }}</small>
                      </td>
                      <td class="bg-proc">
                        <code>{{ candidate.code }}</code><b>{{ candidate.description }}</b>
                      </td>
                      <td>
                        <gloss-code-pill :code="candidate.glossCode"
                                         :description="candidate.glossDescription"/>
                      </td>
                      <td class="text-right tnum text-red">
                        −{{ candidate.openValue | currency }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <div class="cr-panel-foot">
                    <button class="btn btn-link btn-sm" @click="adding.show = false">Fechar</button>
                    <button class="btn btn-primary btn-sm btn-icon btn-icon-left"
                    :disabled="adding.selected.length === 0"
                            @click="addSelected">
                      <fa-icon :icon="['fal', 'arrow-right']"/>Adicionar
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="column col-4 col-md-12">
            <appeal-summary
              :summary="summary"
              :receivable="receivable"
              :receivable-after="receivableAfter"
              :transfers="transferLines"
              :deadline="batch.deadline"
              :pending="pending"
              :batch-number="batch.number"
              :round="round"
              :xml-available="xmlAvailable"
              @goto-pending="gotoPending"
            />
          </div>
        </div>

        <div class="stenci-group stenci-alert bg-send-box" v-if="sendBox.show">
          <div class="columns">
            <div class="column col-3 col-sm-12 form-group"
                 :class="{'has-error': $v.sendBox.sentAt.$error}">
              <label class="form-label">Enviado em</label>
              <dx-input-date id="appeal-sent-at" class="form-input" v-model="sendBox.sentAt"
                             @blur="$v.sendBox.sentAt.$touch()"/>
              <template v-if="$v.sendBox.sentAt.$error">
                <div class="form-input-hint" v-if="!$v.sendBox.sentAt.required">
                  Campo obrigatório
                </div>
                <div class="form-input-hint" v-else-if="!$v.sendBox.sentAt.maxDate">
                  Não pode ser futura
                </div>
                <div class="form-input-hint" v-else>Data inválida</div>
              </template>
            </div>
            <div class="column col-5 col-sm-12 form-group">
              <label class="form-label">
                Nº do recurso na operadora <span class="text-gray">(opcional)</span>
              </label>
              <input type="text" class="form-input" v-model="sendBox.operatorNumber" maxlength="20"
                     placeholder="Protocolo de recebimento do recurso">
            </div>
            <div class="column col-4 col-sm-12 form-group bg-send-actions">
              <button class="btn btn-primary btn-icon btn-icon-left" :class="{ loading: sending }"
                      :disabled="sending" @click="confirmSend">
                <fa-icon :icon="['fal', 'paper-plane']"/>Confirmar envio
              </button>
              <button class="btn btn-link" :disabled="sending" @click="sendBox.show = false">
                Voltar
              </button>
            </div>
          </div>
          <div class="text-xs">
            {{ items.length === 1 ? 'A glosa passa' : `As ${items.length} glosas passam` }}
            a <b>Em recurso</b>
            e o lote a <b>Em recurso</b>. O XML gerado fica congelado no recurso.
          </div>
        </div>
      </template>
    </div>

    <template slot="footer">
      <template v-if="!loading && view">
        <template v-if="status === 'draft'">
          <button class="btn btn-primary btn-icon btn-icon-left mr-1" :class="{ loading: saving }"
                  :disabled="busy || !canManage" @click="saveDraft">
            <fa-icon :icon="['fal', 'save']"/>Salvar rascunho
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left mr-1 tooltip tooltip-top"
          v-if="xmlAvailable"
                  :data-tooltip="pending.length ? 'Resolva as pendências' : null"
                  :class="{ loading: generating }"
                  :disabled="busy || pending.length > 0 || !canManage"
                  @click="generateXml">
            <fa-icon :icon="['fal', 'cloud-download']"/>Gerar XML
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left mr-1 tooltip tooltip-top"
                  :data-tooltip="pending.length ? 'Resolva as pendências' : null"
                  :disabled="busy || pending.length > 0 || sendBox.show || !canManage"
                  @click="openSendBox">
            <fa-icon :icon="['fal', 'paper-plane']"/>Marcar como enviado
          </button>
          <button class="btn btn-error btn-sm mr-1" v-if="appeal" :class="{ loading: deleting }"
                  :disabled="busy || !canManage" @click="removeDraft">Excluir rascunho</button>
        </template>
        <template v-else-if="status === 'sent'">
          <button class="btn btn-primary btn-icon btn-icon-left mr-1" :disabled="!canManage"
                  @click="respond">
            <fa-icon :icon="['fal', 'reply']"/>Registrar resposta
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left mr-1" v-if="xmlAvailable"
                  :class="{ loading: generating }" :disabled="generating" @click="downloadXml">
            <fa-icon :icon="['fal', 'cloud-download']"/>Baixar XML
          </button>
        </template>
        <template v-else-if="status === 'answered'">
          <button class="btn btn-primary btn-icon btn-icon-left mr-1 tooltip tooltip-top"
                  :data-tooltip="secondRoundReason"
                  :disabled="!!secondRoundReason" @click="startSecondRound">
            <fa-icon :icon="['fal', 'gavel']"/>Montar 2ª instância
          </button>
          <button class="btn btn-gray btn-icon btn-icon-left mr-1" v-if="xmlAvailable"
                  :class="{ loading: generating }" :disabled="generating" @click="downloadXml">
            <fa-icon :icon="['fal', 'cloud-download']"/>Baixar XML
          </button>
        </template>
      </template>
      <button class="btn" :disabled="busy" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import {
  required, requiredIf, minValue, maxLength,
} from 'vuelidate/lib/validators';
import { date, maxDate } from '@/data/validators';
import { MANAGE_BILLING_GLOSSES } from '@/data/actions/modules/financial';
import { getName as appealStatusName, getRoundName } from '@/data/billing-appeal-statuses';
import GlossCodeAutocomplete from '../components/GlossCodeAutocomplete.vue';
import GlossCodePill from '../components/GlossCodePill.vue';
import AppealItemCard from '../components/AppealItemCard.vue';
import AppealSummary from '../components/AppealSummary.vue';
import {
  deadlineInfo, fillTemplate, guideLabel, idOf, num, recoveryEffect, round2,
} from '../helpers';

const MAX_LENGTH = 500;
const XML_MIN_VERSION = 40100;
const XML_CURRENT_VERSION = 40300;

const STATUS_CLASSES = {
  draft: 'bg-pill-gray',
  sent: 'bg-pill-amber',
  answered: 'bg-pill-green',
  canceled: 'bg-pill-gray bg-pill-strike',
};

function versionDigits(version) {
  const digits = String(version || '').replace(/\D/g, '');
  return digits ? Number(digits.padEnd(5, '0').slice(0, 5)) : 0;
}

export default {
  components: {
    GlossCodeAutocomplete,
    GlossCodePill,
    AppealItemCard,
    AppealSummary,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    batchId: {
      type: String,
      required: true,
    },
    appealId: {
      type: String,
      default: null,
    },
    glossIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      maxLength: MAX_LENGTH,
      loading: true,
      saving: false,
      generating: false,
      sending: false,
      deleting: false,
      changed: false,
      view: null,
      financial: null,
      appeal: null,
      form: {
        objectType: 'guide',
        operatorNumber: '',
        appealAt: moment().format('YYYY-MM-DD'),
        protocolAppeal: { glossCode: null, justification: '' },
      },
      items: [],
      justifications: {},
      loadingCode: null,
      addedMessage: null,
      adding: { show: false, selected: [] },
      sendBox: { show: false, sentAt: moment().format('YYYY-MM-DD'), operatorNumber: '' },
    };
  },
  computed: {
    canManage() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    busy() {
      return this.saving || this.generating || this.sending || this.deleting;
    },
    batch() {
      return this.view ? this.view.batch : null;
    },
    insuranceId() {
      return this.batch ? idOf(this.batch.insurance) : null;
    },
    insuranceName() {
      const insurance = this.batch && this.batch.insurance;
      if (!insurance) return '';
      const plan = insurance.plan && insurance.plan.name;
      return plan && plan !== insurance.name ? `${insurance.name} · ${plan}` : insurance.name;
    },
    status() {
      return this.appeal ? this.appeal.status : 'draft';
    },
    readonly() {
      return this.status !== 'draft';
    },
    round() {
      if (this.appeal) return num(this.appeal.round) || 1;
      const rounds = ((this.view && this.view.appeals) || []).map(appeal => num(appeal.round));
      return (rounds.length ? Math.max(...rounds) : 0) + 1;
    },
    roundName() {
      return getRoundName(this.round);
    },
    statusName() {
      return appealStatusName(this.status);
    },
    statusClass() {
      return STATUS_CLASSES[this.status] || 'bg-pill-gray';
    },
    title() {
      return this.batch ? `Recurso de glosa — Lote ${this.batch.number}` : 'Recurso de glosa';
    },
    tissVersion() {
      if (this.appeal && this.appeal.tissVersion) return this.appeal.tissVersion;
      return this.batch && this.batch.tiss ? this.batch.tiss.version : null;
    },
    xmlAvailable() {
      if (this.appeal) return !!this.appeal.xmlAvailable;
      return !!(this.batch && this.batch.tiss && this.batch.tiss.enabled)
        && versionDigits(this.tissVersion) >= XML_MIN_VERSION;
    },
    tissOld() {
      return !this.xmlAvailable;
    },
    tissWarn() {
      const version = versionDigits(this.tissVersion);
      return this.xmlAvailable && version < XML_CURRENT_VERSION;
    },
    deadlineInfo() {
      return deadlineInfo(this.batch ? this.batch.deadline : null);
    },
    deadlineOverdue() {
      return this.deadlineInfo.tone === 'overdue';
    },
    receivable() {
      return (this.financial && this.financial.receivable) || null;
    },
    glossMap() {
      const map = new Map();
      if (!this.view) return map;
      this.view.guides.forEach((guide) => {
        guide.items.forEach((item) => {
          item.glosses.forEach((gloss) => {
            map.set(idOf(gloss), { gloss, guide, item });
          });
        });
      });
      return map;
    },
    groups() {
      const groups = [];
      this.items.forEach((item, index) => {
        let group = groups.find(entry => entry.accountId === item.accountId);
        if (!group) {
          group = {
            accountId: item.accountId,
            guideName: item.guideName,
            patientName: item.patientName,
            senha: item.senha,
            items: [],
          };
          groups.push(group);
        }
        group.items.push({ item, index });
      });
      return groups;
    },
    available() {
      if (!this.view) return [];
      const inAppeal = new Set(this.items.map(item => item.glossId));
      const allowSecond = !!this.batch.allowSecondAppeal;
      return [...this.glossMap.values()]
        .filter(({ gloss }) => !inAppeal.has(idOf(gloss)))
        .filter(({ gloss }) => gloss.status === 'open'
          || (allowSecond && gloss.status === 'maintained' && num(gloss.appeal && gloss.appeal.round) < this.round))
        .map(({ gloss, guide, item }) => this.buildItem(gloss, guide, item, null));
    },
    summary() {
      const glossed = round2(this.items.reduce((sum, item) => sum + num(item.openValue), 0));
      const appealed = round2(this.items
        .reduce((sum, item) => sum + Math.min(num(item.appealedValue), num(item.openValue)), 0));
      return {
        items: this.items.length,
        guides: this.groups.length,
        glossed,
        appealed,
        percent: glossed > 0 ? Math.round((appealed / glossed) * 100) : 0,
      };
    },
    receivableAfter() {
      if (!this.receivable) return 0;
      return round2(num(this.receivable.value.total) + this.summary.appealed);
    },
    transferLines() {
      const transfers = (this.financial && this.financial.transfers) || [];
      const bundles = new Map();
      this.items.forEach((item) => {
        if (!item.transfer || !num(item.transfer.amount)) return;
        const credit = recoveryEffect({
          amount: item.transfer.amount,
          insurance: item.informed,
          value: Math.min(num(item.appealedValue), num(item.openValue)),
        });
        if (credit <= 0) return;
        const bundleId = item.transfer.financialBundleId
          ? String(item.transfer.financialBundleId)
          : null;
        const financial = bundleId
          ? transfers.find(doc => String(doc.bundleId) === bundleId) || null
          : null;
        const key = bundleId || `professional:${idOf(item.professional)}`;
        const current = bundles.get(key) || {
          key,
          name: (financial && financial.entity && financial.entity.name)
            || (item.professional && item.professional.name) || 'Profissional',
          paid: financial ? financial.status === 'paid' : false,
          credit: 0,
        };
        current.credit = round2(current.credit + credit);
        bundles.set(key, current);
      });
      return [...bundles.values()];
    },
    pending() {
      const list = [];
      if (this.form.objectType === 'protocol') {
        if (!this.form.protocolAppeal.glossCode) list.push({ kind: 'code', target: 'protocol' });
        if (!String(this.form.protocolAppeal.justification || '').trim()) list.push({ kind: 'justification', target: 'protocol' });
        if (this.items.length === 0) list.push({ kind: 'items', target: null });
        return list;
      }
      if (this.items.length === 0) list.push({ kind: 'items', target: null });
      this.items.forEach((item) => {
        const value = num(item.appealedValue);
        if (value <= 0 || value > num(item.openValue) + 0.004) list.push({ kind: 'value', target: item.glossId });
        if (!String(item.justification || '').trim()) list.push({ kind: 'justification', target: item.glossId });
        if (item.saveJustification && !String(item.saveTitle || '').trim()) list.push({ kind: 'title', target: item.glossId });
      });
      return list;
    },
    protocolLength() {
      return String(this.form.protocolAppeal.justification || '').length;
    },
    secondRoundReason() {
      if (!this.canManage) return 'Sem permissão';
      if (!this.batch || !this.batch.allowSecondAppeal) return 'Convênio não aceita 2ª instância';
      if (this.maintainedGlossIds.length === 0) return 'Nenhum item mantido';
      const pendingAppeal = ((this.view && this.view.appeals) || [])
        .find(appeal => ['draft', 'sent'].includes(appeal.status));
      if (pendingAppeal) return `Já existe o recurso ${pendingAppeal.number}`;
      return null;
    },
    maintainedGlossIds() {
      return this.items
        .filter((item) => {
          const entry = this.glossMap.get(item.glossId);
          return entry && entry.gloss.status === 'maintained';
        })
        .map(item => item.glossId);
    },
  },
  validations() {
    const isProtocol = () => this.form.objectType === 'protocol';
    return {
      form: {
        appealAt: { required, date },
        protocolAppeal: {
          glossCode: { required: requiredIf(isProtocol) },
          justification: { required: requiredIf(isProtocol), maxLength: maxLength(MAX_LENGTH) },
        },
      },
      items: {
        $each: {
          appealedValue: {
            required,
            minValue: minValue(0.01),
            maxValue: (value, item) => num(value) <= num(item.openValue) + 0.004,
          },
          justification: {
            required: requiredIf(() => !isProtocol()),
            maxLength: maxLength(MAX_LENGTH),
          },
          saveTitle: { required: requiredIf('saveJustification') },
        },
      },
      sendBox: {
        sentAt: { required, date, maxDate: maxDate(moment().format('YYYY-MM-DD')) },
      },
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      const requests = [
        this.$http.get('/billing-glosses', { params: { batchId: this.batchId, onlyGlossed: false } }),
        this.$http.get(`/billing-batches/${this.batchId}/financial`).catch(() => ({ data: null })),
        this.appealId ? this.$http.get(`/billing-appeals/${this.appealId}`) : Promise.resolve({ data: null }),
      ];
      return Promise.all(requests)
        .then(([view, financial, appeal]) => {
          this.view = view.data;
          this.financial = financial.data;
          this.applyAppeal(appeal.data);
          if (appeal.data && this.glossIds.length > 0 && appeal.data.status === 'draft') {
            const added = this.addGlossIds(this.glossIds);
            if (added > 0) {
              this.addedMessage = `${added} ${added === 1 ? 'item adicionado' : 'itens adicionados'} ao rascunho ${appeal.data.number}. Salve para gravar.`;
            }
          } else if (!appeal.data) {
            this.addGlossIds(this.glossIds);
          }
        })
        .catch((e) => {
          this.$toast.error(e);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyAppeal(appeal) {
      this.appeal = appeal;
      if (!appeal) return;
      this.form.objectType = appeal.objectType || 'guide';
      this.form.operatorNumber = appeal.operatorNumber || '';
      this.form.appealAt = appeal.dates && appeal.dates.appealAt
        ? moment(appeal.dates.appealAt).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      const protocolAppeal = appeal.protocolAppeal || {};
      this.form.protocolAppeal = {
        glossCode: protocolAppeal.glossCode ? { code: protocolAppeal.glossCode, description: '' } : null,
        justification: protocolAppeal.justification || '',
      };
      this.items = (appeal.guides || []).flatMap(guide => guide.items.map((existing) => {
        const entry = this.glossMap.get(String(existing.glossId));
        if (!entry) {
          return this.buildOrphanItem(existing, guide);
        }
        return this.buildItem(entry.gloss, entry.guide, entry.item, {
          ...existing, senha: guide.senha,
        });
      }));
    },
    buildItem(gloss, guide, item, existing) {
      const openValue = round2(num(gloss.value) - num(gloss.recoveredValue));
      const executionDate = item && item.executionDate ? moment(item.executionDate).format('DD/MM/YYYY') : '';
      return {
        glossId: idOf(gloss),
        accountId: String(guide.accountId),
        itemId: item ? String(item.itemId) : null,
        sequencialItem: item ? item.sequencialItem : null,
        code: item && item.expense ? item.expense.code : gloss.code,
        description: item && item.expense ? item.expense.name : gloss.description,
        meta: item ? [`qtd ${item.quantity}`, executionDate].filter(Boolean).join(' · ') : null,
        glossCode: gloss.code,
        glossDescription: gloss.description,
        informed: item ? num(item.values.informed) : num(gloss.informedValue),
        glossed: num(gloss.value),
        recovered: num(gloss.recoveredValue),
        openValue,
        appealedValue: existing && existing.appealedValue !== null
          && existing.appealedValue !== undefined
          ? num(existing.appealedValue)
          : openValue,
        justification: (existing && existing.justification) || '',
        justificationId: existing && existing.justificationId
          ? String(existing.justificationId)
          : null,
        saveJustification: false,
        saveTitle: '',
        response: (existing && existing.response) || null,
        transfer: item ? item.transfer : null,
        professional: item ? item.professional : (guide.professional || null),
        guideName: guideLabel(guide),
        patientName: (guide.patient && guide.patient.name) || '',
        senha: (existing && existing.senha) || null,
        context: {
          paciente: (guide.patient && guide.patient.name) || '',
          guia: guideLabel(guide).replace('Guia ', ''),
          procedimento: item && item.expense ? item.expense.name : '',
          senha: (existing && existing.senha) || '',
          data: executionDate,
        },
      };
    },
    buildOrphanItem(existing, guide) {
      const account = guide.account || {};
      return {
        glossId: String(existing.glossId),
        accountId: String(guide.accountId),
        itemId: existing.itemId ? String(existing.itemId) : null,
        sequencialItem: existing.sequencialItem,
        code: existing.code,
        description: existing.description,
        meta: null,
        glossCode: existing.glossCode,
        glossDescription: existing.gloss ? existing.gloss.description : null,
        informed: num(existing.analysis && existing.analysis.informed),
        glossed: num(existing.gloss && existing.gloss.value),
        recovered: num(existing.gloss && existing.gloss.recoveredValue),
        openValue: num(existing.appealedValue),
        appealedValue: num(existing.appealedValue),
        justification: existing.justification || '',
        justificationId: existing.justificationId ? String(existing.justificationId) : null,
        saveJustification: false,
        saveTitle: '',
        response: existing.response || null,
        transfer: null,
        professional: null,
        guideName: `Guia ${guide.numeroGuiaOrigem || ''}`,
        patientName: (account.patient && account.patient.name) || '',
        senha: guide.senha || null,
        context: {},
      };
    },
    addGlossIds(ids) {
      let added = 0;
      ids.forEach((id) => {
        const key = String(id);
        if (this.items.some(item => item.glossId === key)) return;
        const entry = this.glossMap.get(key);
        if (!entry) return;
        this.items.push(this.buildItem(entry.gloss, entry.guide, entry.item, null));
        added += 1;
      });
      if (added > 0) this.changed = true;
      return added;
    },
    addSelected() {
      const added = this.addGlossIds(this.adding.selected);
      this.adding = { show: false, selected: [] };
      if (added > 0) this.$toast.success(`${added} ${added === 1 ? 'item adicionado' : 'itens adicionados'} ao recurso.`);
    },
    removeItem(item) {
      this.items = this.items.filter(entry => entry.glossId !== item.glossId);
      this.changed = true;
    },
    sameCodeCount(item) {
      return this.items
        .filter(entry => entry.glossCode === item.glossCode && entry.glossId !== item.glossId)
        .length;
    },
    loadJustifications(code) {
      if (this.justifications[code] || this.loadingCode === code) return Promise.resolve();
      this.loadingCode = code;
      return this.$http.get('/billing-appeal-justifications', { params: { code } })
        .then(({ data }) => {
          this.$set(this.justifications, code, data.items || []);
        })
        .catch(() => {
          this.$set(this.justifications, code, []);
        })
        .finally(() => {
          this.loadingCode = null;
        });
    },
    removeJustification(entry) {
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Excluir a justificativa "${entry.title}" da biblioteca?</div></div>`,
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.$http.delete(`/billing-appeal-justifications/${entry.id}`)
                .then(() => {
                  this.$set(this.justifications, entry.code, (this.justifications[entry.code] || [])
                    .filter(item => item.id !== entry.id));
                  this.$toast.success('Justificativa excluída.');
                })
                .catch((e) => {
                  this.$toast.error(e);
                });
            },
          },
        ],
      });
    },
    applyToCode({ code, text, justificationId }) {
      const library = (this.justifications[code] || [])
        .find(entry => entry.id === justificationId) || null;
      let count = 0;
      this.items.forEach((item) => {
        if (item.glossCode !== code) return;
        const filled = library ? fillTemplate(library.text, item.context) : text;
        if (item.justification === filled) return;
        item.justification = filled;
        item.justificationId = justificationId || null;
        count += 1;
      });
      if (count > 0) this.$toast.success(`Justificativa aplicada a ${count} ${count === 1 ? 'item' : 'itens'} com o código ${code}.`);
    },
    gotoPending() {
      const first = this.pending.find(entry => entry.target && entry.target !== 'protocol');
      if (!first) return;
      const [ref] = this.$refs[`item-${first.target}`] || [];
      if (ref && ref.$el) {
        ref.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const field = ref.$el.querySelector(first.kind === 'value' ? 'input' : 'textarea');
        if (field) field.focus();
      }
    },
    validate() {
      this.$v.form.$touch();
      this.$v.items.$touch();
      if (this.$v.form.$error || this.$v.items.$error || this.items.length === 0) {
        this.$toast.error('Corrija as pendências antes de continuar.');
        this.gotoPending();
        return false;
      }
      return true;
    },
    saveJustifications() {
      const targets = this.items
        .filter(item => item.saveJustification && item.saveTitle && item.justification);
      return targets.reduce((chain, item) => chain.then(() => this.$http.post('/billing-appeal-justifications', {
        code: item.glossCode,
        title: item.saveTitle.trim(),
        text: item.justification.trim(),
      })
        .then(({ data }) => {
          item.justificationId = idOf(data);
          item.saveJustification = false;
          item.saveTitle = '';
          this.$set(this.justifications, item.glossCode, [
            data, ...(this.justifications[item.glossCode] || []),
          ]);
          this.$toast.success(`Justificativa salva para o código ${item.glossCode}. Ela aparece nos próximos recursos.`);
        })), Promise.resolve());
    },
    payload() {
      const isProtocol = this.form.objectType === 'protocol';
      return {
        objectType: this.form.objectType,
        operatorNumber: this.form.operatorNumber.trim() || null,
        appealAt: this.form.appealAt,
        protocolAppeal: isProtocol
          ? {
            glossCode: this.form.protocolAppeal.glossCode
              ? this.form.protocolAppeal.glossCode.code
              : null,
            justification: this.form.protocolAppeal.justification.trim(),
          }
          : null,
        items: this.items.map(item => ({
          glossId: item.glossId,
          appealedValue: round2(item.appealedValue),
          justification: isProtocol ? (item.justification || null) : item.justification.trim(),
          justificationId: item.justificationId || null,
        })),
      };
    },
    persist() {
      const body = this.payload();
      const request = this.appeal
        ? this.$http.put(`/billing-appeals/${this.appeal.id}`, body)
        : this.$http.post('/billing-appeals', {
          ...body,
          batchId: this.batchId,
          glossIds: this.items.map(item => item.glossId),
        });
      return request
        .then(({ data }) => this.$http.get(`/billing-appeals/${idOf(data)}`))
        .then(({ data }) => {
          this.applyAppeal(data);
          this.addedMessage = null;
          this.changed = true;
          return data;
        });
    },
    saveDraft() {
      if (!this.validate()) return Promise.resolve(null);
      this.saving = true;
      return this.saveJustifications()
        .then(() => this.persist())
        .then((appeal) => {
          this.$toast.success(`Rascunho ${appeal.number} salvo.`);
          return appeal;
        })
        .catch((e) => {
          this.$toast.error(e);
          return null;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    generateXml() {
      if (!this.validate()) return Promise.resolve();
      this.generating = true;
      return this.saveJustifications()
        .then(() => this.persist())
        .then(appeal => this.$file.download(
          `/billing-appeals/${appeal.id}/xml`,
          {},
          { name: `RECURSO_${this.batch.number}_R${appeal.round}.xml` },
        ))
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.generating = false;
        });
    },
    downloadXml() {
      if (!this.appeal) return Promise.resolve();
      this.generating = true;
      return this.$file.download(
        `/billing-appeals/${this.appeal.id}/xml`,
        {},
        { name: `RECURSO_${this.batch.number}_R${this.appeal.round}.xml` },
      )
        .catch(() => {})
        .finally(() => {
          this.generating = false;
        });
    },
    openSendBox() {
      if (!this.validate()) return;
      this.sendBox = {
        show: true,
        sentAt: moment().format('YYYY-MM-DD'),
        operatorNumber: this.form.operatorNumber || '',
      };
    },
    confirmSend() {
      this.$v.sendBox.$touch();
      if (this.$v.sendBox.$error || !this.validate()) return Promise.resolve();
      this.sending = true;
      return this.saveJustifications()
        .then(() => this.persist())
        .then(appeal => this.$http.post(`/billing-appeals/${appeal.id}/send`, {
          sentAt: this.sendBox.sentAt,
          operatorNumber: this.sendBox.operatorNumber.trim() || null,
        }))
        .then(({ data }) => {
          this.$toast.success(`Recurso ${data.number} marcado como enviado.`);
          this.$emit('sent', data);
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.sending = false;
        });
    },
    removeDraft() {
      if (!this.appeal) return;
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente excluir o rascunho ${this.appeal.number}? As glosas continuam abertas.</div></div>`,
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.deleting = true;
              this.$http.delete(`/billing-appeals/${this.appeal.id}`)
                .then(() => {
                  this.$toast.success('Rascunho excluído.');
                  this.close(true);
                })
                .catch((e) => {
                  this.$toast.error(e);
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    respond() {
      if (!this.appeal) return;
      this.$emit('respond', String(this.appeal.id));
    },
    startSecondRound() {
      const ids = [...this.maintainedGlossIds];
      this.appeal = null;
      this.items = [];
      this.form = {
        objectType: 'guide',
        operatorNumber: '',
        appealAt: moment().format('YYYY-MM-DD'),
        protocolAppeal: { glossCode: null, justification: '' },
      };
      this.$v.$reset();
      this.addGlossIds(ids);
      this.addedMessage = `2ª instância iniciada com ${ids.length} ${ids.length === 1 ? 'item mantido' : 'itens mantidos'}. Revise as justificativas e salve o rascunho.`;
    },
    close(changed = false) {
      this.$emit('close', changed || this.changed);
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

#modal-appeal {
  .appeal-modal {
    .bg-appeal-head {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-sm;
      margin-bottom: $layout-spacing;
    }
    .bg-appeal-box .form-group { margin-bottom: $layout-spacing-sm; }
    .bg-appeal-grid {
      display: grid;
      gap: $layout-spacing-sm $layout-spacing-lg;
      grid-template-columns: auto 1fr 1fr 1fr;
      @media screen and (max-width: $size-sm) { grid-template-columns: 1fr; }
    }
    .bg-appeal-columns { margin-top: $layout-spacing; }
    .bg-guide-sub { font-weight: 400; }
    .bg-add-panel {
      border-style: dashed;
      .cr-panel-foot { justify-content: flex-start; }
    }
    .bg-send-box {
      margin-bottom: 0;
      margin-top: $layout-spacing-lg;
      .columns { margin-bottom: 0; }
      .form-group { margin-bottom: $layout-spacing-sm; }
      .bg-send-actions {
        align-items: flex-end;
        display: flex;
        gap: $layout-spacing-sm;
      }
    }
  }
}
</style>
